// Connection state machine
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  SIGNALING = 'signaling',
  ICE_GATHERING = 'ice_gathering',
  ICE_CONNECTING = 'ice_connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed',
}

// ICE connection policy
export type IcePolicy = 'auto' | 'p2p' | 'relay';

// TURN relay protocol preference
export type TurnProtocol = 'auto' | 'udp' | 'tcp';

// Video codec preference (实验性手动切换)
// 'auto' 保持现有浏览器默认行为（按 host offer 顺序选首个支持的 codec）
// 'av1' 强制 AV1 优先（需 host 支持 libaom 软编，仅在无 GPU host 可用）
// 'h265' / 'h264' 向后兼容 URL 参数 ?codec=h265/h264
export type VideoCodecPreference = 'auto' | 'h264' | 'h265' | 'av1';

// ===== EncoderCatalog（阶段 2 引入） =====
// host 端 6 个编码器自检结果，前端按 catalog 渲染下拉框 + 切换 UI
// 数据源：host server_encoder_catalog 信令消息
export type EncoderId =
  | 'nvenc_av1' | 'nvenc_h265' | 'nvenc_h264'
  | 'libaom_av1' | 'x264_h264' | 'openh264_h264';

export type EncoderCodecName = 'H264' | 'H265' | 'AV1';
export type EncoderAccel = 'GPU' | 'CPU';

export interface EncoderCatalogEntry {
  id: EncoderId;
  codec: EncoderCodecName;
  accel: EncoderAccel;
  available: boolean;
  reason_code: string;       // 短码，前端做本地化映射；available=true 时为空
  priority: number;          // 自动模式按升序选第一个 available 的
  last_probe_at: number;     // unix 秒
  probe_init_ms: number;
  probe_encode_ms: number;
  slow_perf_warn: boolean;   // probe_encode_ms > 16ms：能跑但 60fps 吃力
}

// host 推送的整份 catalog 消息
export interface ServerEncoderCatalogPayload {
  type: 'server_encoder_catalog';
  catalog: EncoderCatalogEntry[];
  active_encoder_id: EncoderId | 'none';
  probe_pending: boolean;
}

// host 推送的运行期降级通知（阶段 3 才会触发，前端先做接收兜底）
export interface EncoderFallbackPayload {
  type: 'encoder_fallback';
  from: EncoderId;
  to: EncoderId | 'none';
  reason_code: string;
  session_will_reconnect?: boolean;
}

// host 对 select_encoder / probe_rerun 的 ack
export interface EncoderAck {
  ok: boolean;
  active_encoder_id?: EncoderId;
  reason_code?: string;
}

// Status bar visibility mode
export type StatusBarVisibility = 'auto' | 'on' | 'off';

// Status bar orientation
export type StatusBarOrientation = 'vertical' | 'horizontal';

// Application configuration
export interface AppConfig {
  // Network
  signaling: 'websocket' | 'http';
  iceServers: RTCIceServer[];
  icePolicy: IcePolicy;  // 替代 enableTurn
  iceTransportPolicy: RTCIceTransportPolicy;
  turnProtocol: TurnProtocol;

  // Video
  videoCodec: VideoCodecPreference;  // 编码器偏好（实验性，默认 auto）

  // Input
  remoteMouseEnabled: boolean;
  remoteKeyboardEnabled: boolean;

  // UI
  theme: 'dark' | 'light';
  showStatusBar: boolean;
  statusBarVisibility: StatusBarVisibility;  // 连接信息显示模式
  statusBarOrientation: StatusBarOrientation;  // 状态栏方向：竖向/横向
  lowLatencyMode: boolean;
  clipboardSync: boolean;

  // Audio
  systemAudioEnabled: boolean;  // 系统声音（桌面音频）
  micAudioEnabled: boolean;     // 远程麦克风

  // Game
  gameMouseMode: boolean;       // 游戏鼠标模式（相对移动+隐藏本地光标+显示远程光标）

  // ===== 码率控制（实验性，全局生效，host 持久化）=====
  // 不写 localStorage：值由 host 推送的 server_config_status 反映
  fixedBitrateEnabled: boolean;       // 固定码率模式开关（关闭=走 BWE）
  fixedBitrateNvencKbps: number;      // NVENC 锁定值（kbps），上限 20000
  fixedBitrateSwKbps: number;         // 软编锁定值（kbps），上限 4000

  // 内网 TURN 测试开关：开启后 host 端会忽略前端公网 TURN，仅用内网 forwarder
  hostInternalTurnOnly: boolean;
}

// 端到端 TURN 单条链路探测结果（「全部测速」深度探测，host→TURN 半段）
export interface TurnPathProbe {
  ok: boolean;       // TURN Allocate 是否成功
  rttMs: number;     // Refresh 平均 RTT(ms)，-1 = 未测/无样本
  lossPct: number;   // 丢包率(%) 0..100，-1 = 未测
}

// host → 前端 internal_turn_status 推送负载
export interface InternalTurnEndpoint {
  ip: string;
  port: number;
  protocols: string[];   // ["udp","tcp"] | []
  reachable: boolean;
  rttMs: number;         // -1 表示未测/失败（浅层 STUN ping 结果）
  lastProbeAt: number;   // Unix ms; 0 表示从未测过
  // 「全部测速」端到端深度探测结果（host 未测过时整个字段缺失）
  deep?: {
    measured: boolean;
    turnHost: string;        // 映射到的公网 TURN 域名
    direct: TurnPathProbe;   // host 直连公网 TURN
    forward: TurnPathProbe;  // host → 内网 forwarder → 公网 TURN
  };
}

export interface InternalTurnStatus {
  enabled: boolean;
  discoveryComplete: boolean;
  errorReason: string;   // "" | "disabled" | "ini-missing" | "port-missing" | "fetch-failed" | "no-ips" | "probe-all-fail"
  endpoints: InternalTurnEndpoint[];
}

// Server statistics from /stats endpoint
export interface ServerStats {
  capture_mode: string;
  display_mode?: string;  // 显示模式: physical 物理显示器 / virtual 虚拟显示器(无显示器时挂载) / none 无显示器降级
  encoder_type: string;
  encoder_name: string;
  gpu_name: string;
  av1_hw?: boolean;  // NVENC 是否支持 AV1 硬编码（Ada Lovelace+ GPU）
  width: number;
  height: number;
  target_fps: number;
  bitrate_kbps: number;           // BWE 分配的目标码率
  actual_encode_kbps?: number;    // 编码器实际输出码率
  gop: number;
  is_using_bgra_direct: boolean;

  platform?: string;  // "w7" | "w10"
  available_resolutions?: string;  // "1920x1080,1280x720,..."

  // CPU 信息
  cpu_model?: string;    // "Intel(R) Xeon(R) CPU E5-2640 0 @ 2.50GHz"
  cpu_cores?: number;    // 物理核心数
  cpu_threads?: number;  // 逻辑线程数
  cpu_usage?: number;    // 当前 CPU 占用率 (0-100)

  // Host 中继信息
  host_relay_server?: string;  // Host 端使用的 TURN 服务器主机名（"rtc.03kan.com" / ""）
  host_active_path?: 'direct' | 'forward' | 'p2p' | 'unknown' | string; // Host 当前 active session 路径
  host_active_session?: string; // Host 当前 active session / raceId

  // Timing (microseconds)
  capture_time_us: number;
  convert_time_us: number;
  encode_time_us: number;
  go_process_time_us: number;
  server_pipeline_latency_us: number;

  // Frame stats
  capture_fps: number;
  encode_fps: number;
  static_frame_ratio?: number;    // 静态帧占比 (0-100)
  capture_fail_count?: number;    // 采集失败累计次数
  frames_encoded?: number;        // 累计编码帧数
  sec_capture_frames?: number;    // 最近 1 秒采集帧数
  sec_encode_frames?: number;     // 最近 1 秒编码帧数
  sec_delivered_frames?: number;  // 最近 1 秒投递给 libwebrtc 的帧数
  total_captured: number;
  total_encoded: number;
  total_dropped: number;
  total_keyframes: number;

  // ===== 诊断字段（Stage 1）— 编码器深度状态 =====
  encoder_qp?: number;                       // -1 = N/A (NVENC AV1 / OpenH264 / libaom AV1 路径)
  frame_size_avg_bytes?: number;             // 最近一帧编码后大小
  frame_size_p95_bytes_30s?: number;         // 占位，Stage 2 做 histogram
  vbv_capped?: boolean;                      // x264 是否撞 1500kb 上限
  vbv_buf_kbits?: number;                    // 当前 VBV 缓冲（NVENC + x264 共用）
  vbv_max_rate_kbps?: number;                // VBV 限速
  vbv_multiplier?: number;                   // x264 才有：2-16；NVENC 填 -1
  capture_to_encode_lag_us_p50?: number;     // 采集→编码 lag p50（微秒）
  capture_to_encode_lag_us_p95?: number;     // 采集→编码 lag p95（微秒）

  // Stage 3A: 细粒度 lag 分段（用于定位 webrtc VSE buffer 来源）
  // 测量点链：capture (frame.timestamp_us) → broadcast (sink->OnFrame 调用栈) →
  //          vse_call (VideoEncoder::Encode 入口) → encode (SharedXxxEncoder::EncodeFrame 入口)
  // broadcast_lag 反映 VideoBroadcaster 同步分发耗时
  // (vse_call_lag - broadcast_lag) 反映 webrtc VSE encoder_queue 调度延迟
  // (encode_lag - vse_call_lag) 反映 NvencVideoEncoder 内部检查耗时（订阅/初始化等）
  capture_to_vse_call_lag_us_p50?: number;
  capture_to_vse_call_lag_us_p95?: number;
  broadcast_to_sink_lag_us_p50?: number;
  broadcast_to_sink_lag_us_p95?: number;
  active_subscriber_count?: number;          // 活跃 subscriber 数

  // ⚠️ 字段语义：连接以来累计值（不是 30s 增量！）
  // 30s 增量请用 statsService.getEvents30s()
  // events_lifetime 是新字段名（更清晰），events_total 是兼容别名（Stage 3 删除）
  events_lifetime?: EventCounters;
  events_total?: EventCounters;  // 兼容旧前端 / Stage 2.x 过渡期

  // ===== Stage 2A: per-engine 完整数据（multi-codec 串扰修复后输出）=====
  // 当前主用编码器（subscriber 最多者；并列时枚举顺序在前的优先）
  dominant_engine?: string;  // "nvenc_h264" | "nvenc_h265" | "nvenc_av1" | "x264" | "openh264" | "libaom_av1"

  // 6 个 engine 的完整数据，Stage 2C Dashboard 用
  per_engine?: {
    nvenc_h264?: PerEngineData;
    nvenc_h265?: PerEngineData;
    nvenc_av1?: PerEngineData;
    x264?: PerEngineData;
    openh264?: PerEngineData;
    libaom_av1?: PerEngineData;
  };

  // ===== EncoderCatalog（阶段 2）=====
  // 来源：host server_encoder_catalog 消息（独立小消息，与 server_stats 分开发送以避免 SCTP 静默丢）
  // StatsService 收到 server_encoder_catalog 后 merge 到 ServerStats，方便 UI 一处订阅
  encoder_catalog?: EncoderCatalogEntry[];
  active_encoder_id?: EncoderId | 'none';
  probe_pending?: boolean;
}

// 事件计数器（连接以来累计；前端做 30s 增量请用 statsService.getEvents30s）
export interface EventCounters {
  bwe_drop_30plus: number;
  vbv_switch: number;
  sudden_drop: number;
  cpu_overload: number;
  keyframe_forced: number;
  encoder_recovery: number;
  large_frame: number;
  frames_skipped_bwe: number;
  frames_skipped_interval_guard: number;
}

// per-engine 诊断数据（Stage 2A 引入）
export interface PerEngineData {
  last_qp: number;                          // -1 = N/A
  last_frame_size_bytes: number;
  vbv_buf_kbits: number;
  vbv_max_rate_kbps: number;
  vbv_multiplier: number;                   // x264: 2-16；其他: -1
  vbv_capped: boolean;
  active_subscriber_count: number;
  ev_bwe_drop_30plus: number;
  ev_vbv_switch: number;
  ev_sudden_drop: number;
  ev_cpu_overload: number;
  ev_keyframe_forced: number;
  ev_encoder_recovery: number;
  ev_large_frame: number;
  ev_frames_skipped_bwe: number;
  ev_frames_skipped_interval_guard: number;

  // Stage 2B.4: NVENC reconfigure readback（仅 NVENC engines，其他永远 0）
  nvenc_reconfig?: {
    last_set_avg_bitrate_kbps: number;
    last_set_max_bitrate_kbps: number;
    last_set_vbv_buffer_size_kbits: number;
    count_total: number;
    failure_count_total: number;     // >0 即异常（reconfigure 历史 bug）
  };

  // Stage 2B.6: NVENC GDR (Intra Refresh) 进度（仅 NVENC engines）
  nvenc_gdr?: {
    intra_refresh_active: boolean;   // 是否启用 GDR
    period_frames: number;           // 一轮刷新的总帧数（fps × 8）
    frame_in_period: number;         // 当前帧在周期内的位置
    progress_pct: number;            // frame_in_period / period × 100，0-99 浮动
  };

  // Stage 2B.1+2: 30s 滑窗 histogram + 按帧类型分桶
  qp_30s?: {
    bins: number[];           // 8 bin: 0-7,8-15,...,48-55,56+
    keyframe_avg: number;     // -1 = 30s 内无 keyframe 样本
    delta_avg: number;
    min: number;
    max: number;
    total_samples: number;
  };
  frame_size_30s?: {
    bins: number[];           // 8 bin: <2K,2-4K,4-8K,8-16K,16-32K,32-64K,64-128K,128K+
    keyframe_avg_bytes: number;
    keyframe_max_bytes: number;
    delta_avg_bytes: number;
    delta_p95_bytes: number;
    total_samples: number;
  };
  frame_type_30s?: {
    iframe_count: number;
    pframe_count: number;
    iframe_total_bytes: number;
    pframe_total_bytes: number;
    iframe_pframe_size_ratio_x100: number;  // I 帧平均 / P 帧平均 × 100
    bitrate_keyframe_share_pct: number;     // I 帧字节占比 0-100
  };

  // Stage 2B.3: per-subscriber 状态数组（ICE Racing 多 PC 时定位 outlier）
  subscribers?: SubscriberMetrics[];

  // Stage 3 后续：完整编码器配置（启动时写入，运行时不变）
  encoder_config?: EncoderConfig;
}

// 单个配置字段
export interface EncoderConfigField {
  k: string;                    // 字段名
  v: string | number;           // 值（已格式化）
  src: 'host' | 'default';      // 来源：host 显式设置 vs 编码器默认/preset
  note?: string;                // tooltip 详细说明
}

// 编码器配置（每个 engine 一份，启动 Configure 后写入）
export interface EncoderConfig {
  fields: EncoderConfigField[];
  codec_type: string;
  runtime_note?: string;        // 运行时变化字段说明
  opaque_note?: string;         // libaom/openh264 内部参数不可见说明
}

// Stage 2B.3: 单个 subscriber 的最近状态
export interface SubscriberMetrics {
  id: number;
  bitrate_kbps: number;             // 该 subscriber 上次上报的 BWE
  framerate_fps: number;
  last_update_age_ms: number;       // 距上次 UpdateRates 多久；-1 = 从未上报
  subscribe_age_ms: number;         // 订阅至今多久
  is_leader: boolean;               // 软编 SharedSoftwareEncoder 才有 leader 概念，NVENC 永远 false
}

// Combined stream statistics
export interface StreamStats {
  // Real-time metrics
  fps: number;
  rtt: number;
  bitrate: number;
  packetLoss: number;
  jitter: number;

  // Display info
  resolution: string;
  hostGpu: string;
  encoderName: string;
  captureMode: string;
  videoCodec: string; // H264 或 H265

  // Latency breakdown (ms)
  latencyBreakdown: {
    capture: number;
    convert: number;
    encode: number;
    network: number;
    jitterBuffer: number;
    decode: number;
    total: number;
  };

  // Frame counters
  framesDecoded: number;
  framesReceived: number;
  framesDropped: number;
  keyframesDecoded: number;

  // Packet counters
  packetsReceived: number;
  nackCount: number;
  pliCount: number;
  freezeCount: number;
  totalFreezesDuration: number;

  // Connection info
  transportType: 'p2p' | 'relay' | 'unknown';
  localCandidateType: string;
  remoteCandidateType: string;
  remoteAddress: string;
  relayProtocol: string;  // TCP/UDP for relay connections
  relayServer: string;    // TURN 服务器主机名（仅中转时有值）

  // Host info
  hostVersion: string;
  hostPlatform: string;  // "w7" | "w10" | ""

  // Available resolutions (from server)
  availableResolutions: string;  // "1920x1080,1280x720,..."
}

// Log entry for diagnostics
export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
}

// Toast notification
export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

// Input state
export interface InputState {
  mouse: boolean;
  keyboard: boolean;
}

// Mouse input message
export interface MouseInputMessage {
  type: 'mouse';
  x: number;
  y: number;
  eventType: number; // 0: move, 1: down, 2: up, 3: wheel
  button?: number;   // 1: left, 2: right, 4: middle
  wheelDelta?: number;
  relative?: boolean;  // 是否相对位移模式
  deltaX?: number;     // 相对X位移
  deltaY?: number;     // 相对Y位移
}

// Keyboard input message
export interface KeyboardInputMessage {
  type: 'keyboard';
  vkCode: number;
  scanCode: number;
  keyEvent: number; // 0: down, 1: up
  extended: boolean;
}

// Clipboard message
export interface ClipboardMessage {
  type: 'clipboard';
  action: 'copy' | 'paste' | 'sync' | 'enable' | 'disable';  // copy: client->server, paste: request from server, sync: server->client, enable/disable: control sync
  text?: string;
}

// Audio config message
export interface AudioConfigMessage {
  type: 'audio_config';
  systemEnabled: boolean;  // System audio (desktop sound)
  micEnabled: boolean;     // Remote microphone
}

// Cursor info from server
export interface CursorInfo {
  x: number;
  y: number;
  visible: boolean;
  cursorType: number;
  cursorCSS: string;
  width?: number;
  height?: number;
  hotspotX?: number;
  hotspotY?: number;
  cursorId: number;
  imageData?: string;
}

// WebRTC signaling messages
export interface SignalingMessage {
  type: string;
  kind?: string;
  raceId?: string;  // ICE Racing: race session ID
  message?: {
    type?: string;
    sdp?: string;
    candidate?: string;
    sdpMid?: string;
    sdpMLineIndex?: number;
  } | string; // string for server_log type
  roomId?: string;
  sid?: string;
  // ICE Racing fields
  candidates?: Array<{
    raceId: string;
    label: string;
    iceServers: Array<{
      urls: string;
      username?: string;
      credential?: string;
    }>;
    iceTransportPolicy?: string;
  }>;
  raceIds?: string[];
  winnerId?: string;
  winnerType?: string;
  connectTimeMs?: number;
}

// Available capture/encoder modes from server
export interface AvailableModes {
  capture_modes: string[];
  encoder_modes: string[];
  current_capture: string;
  current_encoder: string;
}

// Reconnection strategy
export interface ReconnectStrategy {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitter: number;
}

// Server configuration
export interface ServerConfig {
  id: string;
  name: string;
  host: string;
  port: number;
  wsUrl: string;
}

// File Transfer Types

export enum FileTransferStatus {
  PENDING = 'pending',
  TRANSFERRING = 'transferring',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum FileTransferDirection {
  UPLOAD = 'upload',
  DOWNLOAD = 'download',
}

export interface FileTransferItem {
  id: string;
  name: string;
  path: string;
  size: number;
  isDirectory: boolean;
  transferred: number;
  status: FileTransferStatus;
}

export interface FileTransferTask {
  id: string;
  direction: FileTransferDirection;
  files: FileTransferItem[];
  totalSize: number;
  transferred: number;
  status: FileTransferStatus;
  speed: number;
  startTime: number;
  currentFileIndex: number;
}

export interface FileTransferStats {
  speed: number;
  transferred: number;
  totalSize: number;
  elapsedTime: number;
  remainingTime: number;
  progress: number;
}

// File Search Index Status
export enum FileSearchStatus {
  UNINITIALIZED = 'uninitialized',
  INDEXING = 'indexing',
  BUILDING_INDEX = 'building_index',
  CALCULATING_SIZES = 'calculating_sizes',
  READY = 'ready',
}

export interface FileSearchIndexProgress {
  status: FileSearchStatus;
  progress?: number;        // 0-100 percentage for indexing
  current?: number;         // Current count (e.g., folders processed)
  total?: number;           // Total count
  phase?: string;           // Phase description (e.g., "Phase 1", "Phase 2")
  message?: string;         // Additional message
}
